import { z } from 'zod';

/**
 * Wire conventions — shared-contract.md §3. camelCase everywhere; ISO 8601 with
 * offset and always UTC on the wire; lowercase-snake enums; ids are UUID
 * strings and `code` is the human identifier that appears in URLs.
 *
 * These Zod schemas **are** the contract until the real OpenAPI document
 * exists, and `docs/openapi.json` is generated from them.
 */

/** ISO 8601 with offset. `2026-10-14T06:12:00Z`. */
export const isoDateTime = z.string().datetime({ offset: true });

/** `2026-10-14` */
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected yyyy-MM-dd');

/** `2026-10` */
export const isoMonth = z.string().regex(/^\d{4}-\d{2}$/, 'expected yyyy-MM');

export const uuid = z.string().min(1);

/**
 * `6-A-014` for the real facility — but `config.bays.code` is a bare unique
 * string the backend never promises a shape for (spec/FACILITY.md §3: "do
 * not parse meaning out of it"). The two pilot sensors' bays are genuinely
 * `PILOT-001` / `PILOT-002`, deliberately outside the 362 and outside this
 * pattern, and every `/live/bays` poll includes them for an unscoped
 * account — regex-validating this field made that a permanent parse failure
 * on every single poll, not an edge case.
 */
export const bayCode = z.string().min(1);

/** `6-A` for an area, `Plot 6` for a plot. */
export const zoneCode = z.string().min(1);

/** Stored lowercase hex, 16 characters, no separators. Displayed uppercase. */
export const devEui = z.string().regex(/^[0-9a-f]{16}$/, 'expected 16 lowercase hex characters');

export const bayStateEnum = z.enum(['unknown', 'free', 'occupied', 'fault', 'unavailable']);
export type BayStateWire = z.infer<typeof bayStateEnum>;

export const alertStateEnum = z.enum(['open', 'acknowledged', 'resolved']);
export const severityEnum = z.enum(['minor', 'major', 'critical']);

/**
 * `building` is unused in this facility but is in the data model, so it stays
 * in the enum rather than being dropped.
 */
export const zoneTypeEnum = z.enum(['plot', 'building', 'area']);

/**
 * Derived on the server so the map's health filter and the mobile health dot
 * do not each re-implement the "problem device" predicate (§5.2).
 */
export const deviceHealthEnum = z.enum(['ok', 'battery', 'weak', 'silent', 'hold', 'none']);

/** The problem classification on `/health/devices`. */
export const deviceProblemStateEnum = z.enum([
  'ok',
  'silent',
  'battery',
  'weak',
  'hold',
  'rejecting',
]);

export const MAX_PAGE_SIZE = 200;

/** `{ items, page, pageSize, totalCount }` — shared-contract.md §3. */
export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive().max(MAX_PAGE_SIZE),
    totalCount: z.number().int().nonnegative(),
  });
}

export type Paginated<T> = { items: T[]; page: number; pageSize: number; totalCount: number };

/**
 * Every facility aggregate carries `scopeLimited`. A partial figure must never
 * be presentable as a facility total (§5.2, §9.6).
 */
export const scopeEnvelope = z.object({
  observedAt: isoDateTime,
  scopeLimited: z.boolean(),
});

/** Per-item result envelope — batch writes are never all-or-nothing. */
export const batchResultKind = z.enum(['applied', 'duplicate', 'conflict', 'failed']);

export const problemBody = z
  .object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    detail: z.string().optional(),
    traceId: z.string().optional(),
  })
  .passthrough();
